import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { getStorageUrl } from '../../services/apiService'
import { getAgeFromDob, getCategoryNameForDob } from '../../utils/playerCategory'

const EditPlayer = () => {
  const { playerId } = useParams()
  const navigate = useNavigate()
  const [states, setStates] = useState([])
  const [stateName, setStateName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [masterCategories, setMasterCategories] = useState([])
  const [memberType, setMemberType] = useState('player')
  const [formData, setFormData] = useState({
    full_name: '',
    father_name: '',
    date_of_birth: '',
    gender: 'M',
    blood_group: '',
    height: '',
    weight: '',
    address: '',
    pin_code: '',
    district: '',
    state_id: '',
    position: 'Player',
    aadhar_card: '',
    passport_number: '',
    primary_contact: '',
    secondary_contact: '',
    email: '',
    marital_status: ''
  })
  const [districts, setDistricts] = useState([])

  const positions = [
    'Player', 'Pitcher', 'Catcher', 'First Baseman', 'Second Baseman',
    'Third Baseman', 'Shortstop', 'Left Fielder', 'Center Fielder', 'Right Fielder'
  ]

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

  useEffect(() => {
    fetchPlayerDetails()
    fetchStates()
    fetchMasterCategories()
  }, [playerId])

  const fetchMasterCategories = async () => {
    try {
      const response = await api.get('/user/categories')
      setMasterCategories(response.data.data || [])
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  // Set state name from states list when both are available
  useEffect(() => {
    if (formData.state_id && states.length > 0 && !stateName) {
      const found = states.find(s => String(s.id) === String(formData.state_id))
      if (found) setStateName(found.name)
    }
  }, [formData.state_id, states])

  const fetchPlayerDetails = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/user/my-players/${playerId}`)
      if (response.data.success) {
        const player = response.data.data

        // Prevent editing blocked players
        if (player.status === 'Inactive' || player.status === 'inactive') {
          alert('This player has been blocked by the admin and cannot be edited.')
          navigate('/user/my-players')
          return
        }

        setMemberType(player.member_type || 'player')
        setFormData({
          full_name: player.full_name || '',
          father_name: player.father_name || '',
          date_of_birth: player.date_of_birth || '',
          gender: player.gender?.toUpperCase() || 'M',
          blood_group: player.blood_group || '',
          height: player.height || '',
          weight: player.weight || '',
          address: player.address || '',
          pin_code: player.pin_code || '',
          district: player.district || '',
          state_id: player.state_id || '',
          position: player.position || 'Player',
          aadhar_card: player.aadhar_card || '',
          passport_number: player.passport_number || '',
          primary_contact: player.primary_contact || '',
          secondary_contact: player.secondary_contact || '',
          email: player.email || '',
          marital_status: player.marital_status || ''
        })

        // Fetch districts and state name if state is set
        if (player.state_id) {
          fetchDistricts(player.state_id)
          // Set state name from player data or fetch it
          if (player.state_name) {
            setStateName(player.state_name)
          }
        }

        if (player.player_image) {
          setImagePreview(getStorageUrl(player.player_image))
        }
      }
    } catch (error) {
      console.error('Error fetching player:', error)
      alert('Failed to fetch player details')
      navigate('/user/my-players')
    } finally {
      setLoading(false)
    }
  }

  const fetchStates = async () => {
    try {
      const response = await api.get('/user/states')
      if (response.data.success) {
        setStates(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching states:', error)
    }
  }

  const fetchDistricts = async (stateId) => {
    if (!stateId) return
    try {
      const response = await api.get(`/user/states/${stateId}/districts`)
      if (response.data.success) {
        setDistricts(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching districts:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    // Fetch districts when state changes
    if (name === 'state_id' && value) {
      fetchDistricts(value)
      // Reset district when state changes
      setFormData(prev => ({ ...prev, [name]: value, district: '' }))
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Image size should be less than 2MB' }))
        return
      }

      setImageFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required'
    if (!formData.father_name.trim()) newErrors.father_name = "Father's name is required"
    if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required'
    if (!formData.state_id) newErrors.state_id = 'State is required'
    if (!formData.gender) newErrors.gender = 'Gender is required'

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (formData.primary_contact && !/^\d{10}$/.test(formData.primary_contact)) {
      newErrors.primary_contact = 'Contact must be 10 digits'
    }

    if (formData.secondary_contact && !/^\d{10}$/.test(formData.secondary_contact)) {
      newErrors.secondary_contact = 'Contact must be 10 digits'
    }

    if (formData.aadhar_card && !/^\d{12}$/.test(formData.aadhar_card)) {
      newErrors.aadhar_card = 'Aadhar must be 12 digits'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const currentAge = getAgeFromDob(formData.date_of_birth)
  // Age categories apply to players only; coaches and managers have none
  const isPlayer = memberType === 'player'
  const category = isPlayer ? getCategoryNameForDob(formData.date_of_birth, masterCategories) : ''
  const categoryDisplay = category ||
    (masterCategories.length ? 'No category for this age' : 'Loading categories...')

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      alert('Please fill all required fields correctly')
      return
    }

    setSaving(true)

    try {
      const formDataToSend = new FormData()

      // Add _method for Laravel to recognize this as PUT request
      formDataToSend.append('_method', 'PUT')

      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key])
        }
      })

      // Category follows the DOB, from the same category master as Add Player
      if (category) {
        formDataToSend.append('category', category)
      }

      // Append image if selected
      if (imageFile) {
        formDataToSend.append('player_image', imageFile)
      }

      const response = await api.post(`/user/my-players/${playerId}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        alert('Player updated successfully!')
        navigate('/user/my-players')
      }
    } catch (error) {
      console.error('Error updating player:', error)
      alert(error.response?.data?.message || 'Failed to update player')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="edit-player-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading player details...</p>
        </div>
        <style>{getStyles()}</style>
      </div>
    )
  }

  return (
    <div className="edit-player-page">
      {/* Page Header with gradient background */}
      <div className="page-header-banner">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-user-edit"></i>
          </div>
          <div className="header-text">
            <h1>Edit Player</h1>
            <p>Update player information</p>
          </div>
        </div>
        <button className="btn-back" style={{ marginLeft: 'auto' }} onClick={() => navigate('/user/my-players')}>
          <i className="fas fa-arrow-left"></i>
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Image Upload Section */}
          <div className="image-upload-section">
            <div className="image-preview">
              {imagePreview ? (
                <img src={imagePreview} alt="Player" />
              ) : (
                <div className="placeholder">
                  <i className="fas fa-user-circle"></i>
                  <p>Upload Photo</p>
                </div>
              )}
            </div>
            <div className="image-upload-controls">
              <label className="btn-upload">
                <i className="fas fa-camera"></i> Change Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </label>
              {errors.image && <span className="error-text">{errors.image}</span>}
              <small>JPG, PNG, GIF (Max 2MB)</small>
            </div>
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <h3><i className="fas fa-user"></i> Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter player's full name"
                  className={errors.full_name ? 'error' : ''}
                  required
                />
                {errors.full_name && <span className="error-text">{errors.full_name}</span>}
              </div>

              <div className="form-group">
                <label>Father's Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="father_name"
                  value={formData.father_name}
                  onChange={handleInputChange}
                  placeholder="Enter father's name"
                  className={errors.father_name ? 'error' : ''}
                  required
                />
                {errors.father_name && <span className="error-text">{errors.father_name}</span>}
              </div>

              <div className="form-group">
                <label>Date of Birth <span className="required">*</span></label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split('T')[0]}
                  className={errors.date_of_birth ? 'error' : ''}
                  required
                />
                {errors.date_of_birth && <span className="error-text">{errors.date_of_birth}</span>}
                {currentAge && isPlayer && (
                  <small className="info-text">
                    Age: {currentAge} years - Auto Category: {categoryDisplay}
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Gender <span className="required">*</span></label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={errors.gender ? 'error' : ''}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
                {errors.gender && <span className="error-text">{errors.gender}</span>}
              </div>

              <div className="form-group">
                <label>Blood Group</label>
                <select
                  name="blood_group"
                  value={formData.blood_group}
                  onChange={handleInputChange}
                >
                  <option value="">Select Blood Group</option>
                  {bloodGroups.map(bg => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Height (cm)</label>
                <input
                  type="number"
                  name="height"
                  value={formData.height}
                  onChange={handleInputChange}
                  placeholder="Height in cm"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Weight (kg)</label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder="Weight in kg"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Marital Status</label>
                <select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleInputChange}
                >
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-section">
            <h3><i className="fas fa-phone"></i> Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Contact Number - Primary</label>
                <input
                  type="tel"
                  name="primary_contact"
                  value={formData.primary_contact}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    setFormData(prev => ({ ...prev, primary_contact: value }))
                    if (errors.primary_contact) setErrors(prev => ({ ...prev, primary_contact: '' }))
                  }}
                  placeholder="10-digit primary contact number"
                  maxLength="10"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={errors.primary_contact ? 'error' : ''}
                />
                {errors.primary_contact && <span className="error-text">{errors.primary_contact}</span>}
              </div>

              <div className="form-group">
                <label>Contact Number - Secondary</label>
                <input
                  type="tel"
                  name="secondary_contact"
                  value={formData.secondary_contact}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    setFormData(prev => ({ ...prev, secondary_contact: value }))
                    if (errors.secondary_contact) setErrors(prev => ({ ...prev, secondary_contact: '' }))
                  }}
                  placeholder="10-digit secondary contact number"
                  maxLength="10"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={errors.secondary_contact ? 'error' : ''}
                />
                {errors.secondary_contact && <span className="error-text">{errors.secondary_contact}</span>}
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="member@example.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="form-section">
            <h3><i className="fas fa-map-marker-alt"></i> Address Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter complete address"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>State <span className="required">*</span></label>
                <input
                  type="text"
                  value={stateName || 'Auto-filled'}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label>District</label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  disabled={!formData.state_id || districts.length === 0}
                >
                  <option value="">{formData.state_id ? 'Select District' : 'Select State First'}</option>
                  {districts.map(district => (
                    <option key={district.id} value={district.name}>{district.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>PIN Code</label>
                <input
                  type="text"
                  name="pin_code"
                  value={formData.pin_code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setFormData(prev => ({ ...prev, pin_code: value }))
                  }}
                  placeholder="6-digit PIN code"
                  maxLength="6"
                  pattern="[0-9]*"
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>

          {/* Playing Information */}
          <div className="form-section">
            <h3><i className="fas fa-baseball-ball"></i> Playing Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Position</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                >
                  <option value="">Select Position</option>
                  {positions.map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>

              {currentAge && isPlayer && (
                <div className="form-group">
                  <label>Category (Auto-calculated)</label>
                  <input
                    type="text"
                    value={categoryDisplay}
                    disabled
                    className="auto-field"
                  />
                  <small className="info-text">
                    Category is automatically set based on date of birth
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* Identity Information */}
          <div className="form-section">
            <h3><i className="fas fa-id-card"></i> Identity Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Aadhar Card Number</label>
                <input
                  type="text"
                  name="aadhar_card"
                  value={formData.aadhar_card}
                  onChange={handleInputChange}
                  placeholder="12-digit Aadhar number"
                  maxLength="12"
                  className={errors.aadhar_card ? 'error' : ''}
                />
                {errors.aadhar_card && <span className="error-text">{errors.aadhar_card}</span>}
              </div>

              <div className="form-group">
                <label>Passport Number</label>
                <input
                  type="text"
                  name="passport_number"
                  value={formData.passport_number}
                  onChange={handleInputChange}
                  placeholder="Passport number (if available)"
                  maxLength="20"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/user/my-players')}
              disabled={saving}
            >
              <i className="fas fa-times"></i> Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{getStyles()}</style>
    </div>
  )
}

const getStyles = () => `
  .edit-player-page {
    padding: 0;
    width: 100%;
    min-height: 100vh;
    background: #f3f4f6;
  }

  .page-header-banner {
    background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
    padding: 1.5rem 2rem;
    display: flex;
    align-items: center;
    gap: 1.5rem;
    border-radius: 12px;
    margin: 1.5rem;
    margin-bottom: 0;
  }

  .btn-back {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.3);
    background: rgba(255,255,255,0.1);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: all 0.2s;
    flex-shrink: 0;
  }

  .btn-back:hover {
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.5);
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon {
    width: 50px;
    height: 50px;
    background: rgba(255,255,255,0.15);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .header-icon i {
    font-size: 1.5rem;
    color: white;
  }

  .header-text h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    margin: 0;
  }

  .header-text p {
    color: rgba(255,255,255,0.7);
    margin: 0;
    font-size: 0.875rem;
  }

  .form-container {
    background: white;
    margin: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    overflow: hidden;
  }

  .image-upload-section {
    display: flex;
    gap: 2rem;
    padding: 2rem;
    background: #f9fafb;
    align-items: center;
    border-bottom: 1px solid #e5e7eb;
  }

  .image-preview {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    overflow: hidden;
    border: 4px solid var(--primary-color, #022a5e);
    background: white;
    flex-shrink: 0;
  }

  .image-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    color: #9ca3af;
  }

  .placeholder i {
    font-size: 3rem;
    margin-bottom: 0.5rem;
    color: var(--primary-color, #022a5e);
    opacity: 0.5;
  }

  .placeholder p {
    font-size: 0.875rem;
  }

  .image-upload-controls {
    flex: 1;
  }

  .btn-upload {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: var(--primary-color, #022a5e);
    color: white;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .btn-upload:hover {
    background: var(--primary-hover, #053e88);
  }

  .image-upload-controls small {
    display: block;
    color: #6b7280;
    margin-top: 0.5rem;
  }

  .form-section {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .form-section:last-of-type {
    border-bottom: none;
  }

  .form-section h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 1.25rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .form-section h3 i {
    color: var(--primary-color, #022a5e);
    font-size: 0.9rem;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-group.full-width {
    grid-column: span 2;
  }

  .form-group label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .required {
    color: #ef4444;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.9rem;
    transition: all 0.2s;
  }

  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: var(--primary-color, #022a5e);
    box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
  }

  .form-group input::placeholder,
  .form-group textarea::placeholder {
    color: #9ca3af;
  }

  .form-group input.error,
  .form-group select.error {
    border-color: #ef4444;
  }

  .form-group input.auto-field {
    background: #f3f4f6;
    cursor: not-allowed;
    color: var(--primary-color, #022a5e);
    font-weight: 500;
  }

  .error-text {
    color: #ef4444;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .info-text {
    color: #c00f15;
    font-size: 0.8rem;
    margin-top: 0.25rem;
    font-weight: 500;
  }

  .form-actions {
    padding: 1.5rem 2rem;
    background: #f9fafb;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  .btn-cancel {
    padding: 0.75rem 1.5rem;
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
  }

  .btn-cancel:hover {
    background: #f3f4f6;
    color: #374151;
  }

  .btn-submit {
    padding: 0.75rem 1.5rem;
    background: var(--primary-color, #022a5e);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
  }

  .btn-submit:hover:not(:disabled) {
    background: var(--primary-hover, #053e88);
  }

  .btn-submit:disabled,
  .btn-cancel:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    gap: 1rem;
  }

  .loading-container i {
    font-size: 3rem;
    color: var(--primary-color, #022a5e);
  }

  .loading-container p {
    color: #6b7280;
    font-size: 1.1rem;
  }

  @media (max-width: 768px) {
    .form-container {
      margin: 1rem;
    }

    .form-grid {
      grid-template-columns: 1fr;
    }

    .form-group.full-width {
      grid-column: span 1;
    }

    .form-section {
      padding: 1.25rem 1.5rem;
    }

    .image-upload-section {
      flex-direction: column;
      text-align: center;
    }

    .form-actions {
      flex-direction: column-reverse;
    }

    .btn-submit,
    .btn-cancel {
      width: 100%;
      justify-content: center;
    }
  }
`

export default EditPlayer
